import { SoftSurveyQuestion } from '@chiroup/components/survey/soft';
import { surveyIds } from '@chiroup/core/constants/SurveyIds';
import { Genders } from '@chiroup/core/enums/Genders.enum';
import { surveyHasConditionalLogic } from '@chiroup/core/functions/surveyHasConditionalLogic';
import {
  SurveyBehavesAs,
  SurveyProgress,
  SurveyQuestion,
  SurveyQuestionAnswer,
} from '@chiroup/core/types/Survey.type';

import { FilledCircleCheck } from '@chiroup/components/FilledCircleCheck';
import { Icon } from '@chiroup/components/Icon';
import { TolerantTouchButton } from '@chiroup/components/TolerantTouchButton';
import { Icons } from '@chiroup/components/icons';
import * as Sentry from '@sentry/react';
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { usePatientPortal } from '../../contexts/PatientPortalContext';
import { useInvalidateAppointmentSurveysOnUnmount } from '../../hooks/useAppointmentSurveys';
import surveyService, { SurveyDetail } from '../../services/survey.service';
import { Skeleton } from '../common/Skeleton';
import InterCCS from '../request/InterCCS';
import InterReeval from '../request/InterReeval';

type RunnerStatus = 'loading' | 'intro' | 'question' | 'complete' | 'error' | 'notfound';

/**
 * Slim per-survey runner mounted at `/surveys/:patientSurveyId`. Loads
 * a single PatientSurvey via the Cognito-authed `surveyGet` endpoint,
 * walks the question list using the schedule backend's Cognito-authed
 * answer-submit endpoint, and routes the patient back to Home on
 * completion.
 *
 * Visual style is the "Soft Card" variant — tinted brand card for
 * intro / complete, top progress pill on the question screen, and
 * tappable rounded option cards. Radio (the dominant survey type) is
 * rendered locally; non-radio types fall back to the legacy shared
 * `SurveyQuestionComponent` so checkbox / slider / date / textarea
 * keep working without re-implementing them here.
 */
const SurveyRunner: React.FC<{ basePath: string }> = ({ basePath }) => {
  const { patientSurveyId } = useParams<{ patientSurveyId: string }>();
  const navigate = useNavigate();
  const { selectedPatient } = usePatientPortal();
  const primaryColor = selectedPatient?.clinic?.primaryColor || '#77bd43';
  const clinicPath = (selectedPatient?.clinic as { path?: string } | undefined)?.path;

  // Refresh Home's pre-visit task list when leaving — otherwise the
  // 30s staleTime keeps the cached entry showing as not-complete.
  useInvalidateAppointmentSurveysOnUnmount();

  const [status, setStatus] = useState<RunnerStatus>('loading');
  const [detail, setDetail] = useState<SurveyDetail | null>(null);
  const [currentQuestion, setCurrentQuestion] = useState<SurveyQuestion | null>(null);
  const [previousQuestionId, setPreviousQuestionId] = useState<string | undefined>(undefined);
  const [progress, setProgress] = useState<SurveyProgress | undefined>(undefined);
  const [submitting, setSubmitting] = useState(false);
  const [hideSteps, setHideSteps] = useState(false);

  const language: 'en' | 'es' = (detail?.language === 'es' ? 'es' : 'en') as 'en' | 'es';

  useEffect(() => {
    let cancelled = false;

    if (!clinicPath || !patientSurveyId) {
      setStatus('notfound');

      return;
    }

    setStatus('loading');

    surveyService
      .getSurvey(clinicPath, patientSurveyId)
      .then(res => {
        if (cancelled) return;
        setDetail(res);
        setProgress(res.progress);

        if (res.completedAt || !res.currentQuestionId) {
          setStatus('complete');

          return;
        }

        const startQuestion =
          res.survey.questions.find(q => q.id === res.currentQuestionId) || null;
        setCurrentQuestion(startQuestion);
        setStatus(res.survey.questions.some(question => !!question.answer) ? 'question' : 'intro');
      })
      .catch(err => {
        if (cancelled) return;
        const code = err?.response?.status || err?.statusCode;

        if (code === 404) {
          setStatus('notfound');
        } else {
          Sentry.captureException(err);
          setStatus('error');
        }
      });

    return () => {
      cancelled = true;
    };
  }, [clinicPath, patientSurveyId]);

  const completedAtLabel = useMemo(() => {
    if (!detail?.completedAt) return null;

    return new Date(detail.completedAt).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }, [detail?.completedAt]);

  const start = () => {
    setHideSteps(true);
    setStatus('question');
    setTimeout(() => setHideSteps(false), 250);
  };

  const onSelect = async (val: SurveyQuestionAnswer) => {
    if (!detail || !currentQuestion?.id || !clinicPath) return;

    setSubmitting(true);
    setHideSteps(true);

    try {
      const { question: nextQuestion, progress: nextProgress } = await surveyService.submitAnswer({
        path: clinicPath,
        patientSurveyId: detail.id,
        patientSurveyQuestionId: currentQuestion.id,
        answer: val,
      });

      setProgress(nextProgress);

      if (nextQuestion?.id) {
        setPreviousQuestionId(currentQuestion.id);
        setCurrentQuestion(nextQuestion);
      } else {
        setStatus('complete');
      }
    } catch (err) {
      Sentry.captureException(err);
    } finally {
      setSubmitting(false);
      setHideSteps(false);
    }
  };

  const goBack = async () => {
    if (!detail || !previousQuestionId || !clinicPath) return;

    try {
      const res = await surveyService.getPreviousQuestion({
        path: clinicPath,
        patientSurveyId: detail.id,
        questionId: previousQuestionId,
      });

      setCurrentQuestion(res.question);
      setPreviousQuestionId(res.previousQuestion);
      setProgress(res.progress);
    } catch (err) {
      Sentry.captureException(err);
    }
  };

  // basePath is `''` in the authed portal; React Router treats `to=""`
  // and `navigate('')` as "stay on current path" rather than home, so
  // fall back to `'/'` to actually leave the survey.
  const exitToHome = () => navigate(basePath || '/');

  // Resolve survey behavior from either the explicit `behavesAs` flag
  // (custom surveys) or the well-known surveyId (built-in templates) —
  // portal-issued PatientSurveys arrive without behavesAs.
  const idToBehavesAs: Record<string, SurveyBehavesAs> = {
    [surveyIds.ccs]: SurveyBehavesAs.CCS,
    [surveyIds.abbreviatedCCS]: SurveyBehavesAs.AbbreviatedCCS,
    [surveyIds.reevaluation]: SurveyBehavesAs.Reevaluation,
    [surveyIds.interimVisitSurvey]: SurveyBehavesAs.Interim,
  };
  const effectiveBehavesAs: SurveyBehavesAs | null =
    (detail?.survey?.behavesAs as SurveyBehavesAs | undefined | null) ||
    (detail?.survey?.id ? idToBehavesAs[detail.survey.id] : undefined) ||
    null;
  const isAdditionalComplaintsSurvey = detail?.survey?.id === surveyIds.additionalComplaintSurvey;

  // Reeval owns the entire surface via <ReevalSurvey>; must be checked
  // before the CCS branch (routing reeval to <ChiefComplaintSurvey>
  // skips per-complaint follow-ups).
  const isReeval = !!detail && effectiveBehavesAs === SurveyBehavesAs.Reevaluation;

  const ccsLikeBehavesAs =
    effectiveBehavesAs === SurveyBehavesAs.CCS ||
    effectiveBehavesAs === SurveyBehavesAs.AbbreviatedCCS ||
    isAdditionalComplaintsSurvey;
  const startQuestion = currentQuestion ?? detail?.survey?.questions?.[0];
  // Drive CCS routing strictly off behavesAs / well-known id — NOT off a
  // leading bodypicker. A standard survey can end up with an orphaned body
  // picker as question 1 (e.g. set to CCS then back to standard); routing it
  // as CCS here hijacks the whole survey and skips the custom questions.
  const isCCSLike = !isReeval && !!detail && ccsLikeBehavesAs;

  if (isReeval && detail && startQuestion && detail.requestId) {
    const reevalValidationCode = detail.validationCode ?? '';

    return (
      <div className="flex flex-col min-h-[60vh]">
        <div className="mb-4">
          <Link
            to={basePath || '/'}
            className="inline-flex items-center gap-1 text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
          >
            <Icon icon={Icons.chevronLeft} className="size-4" />
            {language === 'es' ? 'Volver' : 'Back'}
          </Link>
        </div>
        <InterReeval
          requestId={detail.requestId}
          patientSurveyId={detail.id}
          validationCode={reevalValidationCode}
          primaryColor={primaryColor}
          language={language}
          gender={(detail.gender as unknown as Genders) ?? Genders.notDisclosed}
          age={detail.age ?? 0}
          startQuestion={startQuestion}
          ccsFollowups={detail.ccsFollowups}
          prMap={detail.prMap ?? {}}
          complete={exitToHome}
          markSurveyComplete={exitToHome}
          complaints={detail.complaints ?? []}
          allowAdditionalComplaints={detail.survey?.allowAdditionalComplaints !== false}
          customQuestions={
            detail.survey?.behavesAs === SurveyBehavesAs.Reevaluation
              ? detail.survey?.questions
              : undefined
          }
          customCCSSurveyId={
            detail.survey?.behavesAs === SurveyBehavesAs.Reevaluation
              ? detail.survey?.id
              : undefined
          }
          draft={detail.draft}
        />
      </div>
    );
  }

  if (isCCSLike && detail && startQuestion && detail.requestId) {
    // Portal-issued PatientRequests don't carry a validationCode
    // (only legacy direct-request bootstraps do). The legacy
    // whole-survey submit handler tolerates an empty value — it's
    // the only string the schedule wrapper considers acceptable
    // here — so default to '' rather than blocking the dispatch.
    const ccsValidationCode = detail.validationCode ?? '';

    return (
      <div className="flex flex-col min-h-[60vh]">
        <div className="mb-4">
          <Link
            to={basePath || '/'}
            className="inline-flex items-center gap-1 text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
          >
            <Icon icon={Icons.chevronLeft} className="size-4" />
            {language === 'es' ? 'Volver' : 'Back'}
          </Link>
        </div>
        <InterCCS
          requestId={detail.requestId}
          patientSurveyId={detail.id}
          validationCode={ccsValidationCode}
          primaryColor={primaryColor}
          language={language}
          gender={(detail.gender as unknown as Genders) ?? Genders.notDisclosed}
          age={detail.age ?? 0}
          startQuestion={startQuestion}
          ccsFollowups={detail.ccsFollowups}
          prMap={detail.prMap ?? {}}
          complete={exitToHome}
          markSurveyComplete={exitToHome}
          additionalComplaints={isAdditionalComplaintsSurvey}
          abbreviatedCCS={effectiveBehavesAs === SurveyBehavesAs.AbbreviatedCCS}
          doNotAllowUnrelatedRegions={selectedPatient?.clinic?.doNotAllowUnrelatedRegions === true}
          allowAdditionalComplaints={detail.survey?.allowAdditionalComplaints !== false}
          // Only forward question lists for *custom* CCS-family templates.
          // Built-in templates embed only the bodypicker; ChiefComplaintSurvey
          // generates the abbreviated per-region followups itself, and
          // passing the bodypicker-only array would short-circuit that.
          customQuestions={
            detail.survey?.behavesAs === SurveyBehavesAs.CCS ||
            detail.survey?.behavesAs === SurveyBehavesAs.AbbreviatedCCS
              ? detail.survey?.questions
              : undefined
          }
          customCCSSurveyId={
            detail.survey?.behavesAs === SurveyBehavesAs.CCS ||
            detail.survey?.behavesAs === SurveyBehavesAs.AbbreviatedCCS
              ? detail.survey?.id
              : undefined
          }
          currentSurveyId={detail.survey?.id}
          draft={detail.draft}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-[60vh]">
      <div className="mb-4">
        <Link
          to={basePath || '/'}
          className="inline-flex items-center gap-1 text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
        >
          <Icon icon={Icons.chevronLeft} className="size-4" />
          {language === 'es' ? 'Volver' : 'Back'}
        </Link>
      </div>

      {status === 'loading' && (
        <div className="flex flex-col gap-4">
          <Skeleton className="h-8 w-2/3" />
          <Skeleton className="h-4 w-1/2" />
          <Skeleton className="h-40 w-full rounded-2xl" />
        </div>
      )}

      {status === 'notfound' && (
        <div className="portal-card p-6 text-center">
          <p className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-1">
            {language === 'es' ? 'Cuestionario no encontrado' : 'Survey not found'}
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {language === 'es'
              ? 'Es posible que se haya retirado o que no esté disponible para esta cuenta.'
              : 'It may have been withdrawn or be unavailable for this account.'}
          </p>
        </div>
      )}

      {status === 'error' && (
        <div className="portal-card p-6 text-center">
          <p className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-1">
            {language === 'es' ? 'Algo salió mal' : 'Something went wrong'}
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {language === 'es'
              ? 'Por favor inténtelo de nuevo en unos momentos.'
              : 'Please try again in a moment.'}
          </p>
        </div>
      )}

      {status === 'intro' && detail && (
        <SoftIntro
          detail={detail}
          language={language}
          primaryColor={primaryColor}
          onStart={start}
        />
      )}

      {status === 'question' && currentQuestion && (
        <SoftSurveyQuestion
          question={currentQuestion}
          progress={
            progress
              ? {
                  ...progress,
                  // Conditional-logic surveys have a moving total, so show the
                  // bar without the jumping count (Monday 12560530217). Trust
                  // the backend's flag first, fall back to local detection.
                  hideCount:
                    progress.hideCount || surveyHasConditionalLogic(detail?.survey?.questions),
                }
              : progress
          }
          language={language}
          primaryColor={primaryColor}
          disabled={submitting}
          hidden={hideSteps}
          showPrevious={!!previousQuestionId}
          onBack={goBack}
          onSelect={onSelect}
        />
      )}

      {status === 'complete' && detail && (
        <SoftComplete
          detail={detail}
          completedAtLabel={completedAtLabel}
          language={language}
          primaryColor={primaryColor}
          onExit={exitToHome}
        />
      )}
    </div>
  );
};

// -----------------------------------------------------------------------
// Soft Card panels
// -----------------------------------------------------------------------

// (Local SoftQuestion + renderNonRadio removed — every question type
// now goes through the shared `<SoftSurveyQuestion>` from
// `libs/components/survey/soft`. One renderer means a CCS, a portal
// survey, and a public request survey all look identical.)

const SoftIntro: React.FC<{
  detail: SurveyDetail;
  language: 'en' | 'es';
  primaryColor: string;
  onStart: () => void;
}> = ({ detail, language, primaryColor, onStart }) => (
  <div
    className="rounded-3xl overflow-hidden p-6 sm:p-8 dark:ring-1 dark:ring-white/10"
    style={{ backgroundColor: `${primaryColor}12` }}
  >
    <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: primaryColor }}>
      {language === 'es' ? 'Cuestionario' : 'Survey'}
    </p>
    <h1 className="mt-1 text-2xl sm:text-3xl font-bold tracking-tight text-gray-900 dark:text-gray-100">
      {detail.survey.title || detail.survey.name || ''}
    </h1>
    {detail.survey.subtitle && (
      <p className="mt-2 text-sm text-gray-600 dark:text-gray-300 max-w-prose">
        {detail.survey.subtitle}
      </p>
    )}
    {detail.survey.startMessage && (
      <p className="mt-4 text-base text-gray-700 dark:text-gray-200 max-w-prose">
        {detail.survey.startMessage}
      </p>
    )}
    <TolerantTouchButton
      onPress={onStart}
      className="mt-6 inline-flex items-center justify-center rounded-full px-7 py-3 text-sm font-semibold text-white active:scale-[0.98] transition-transform"
      style={{ backgroundColor: primaryColor }}
    >
      {language === 'es' ? 'Empezar' : 'Get started'}
    </TolerantTouchButton>
  </div>
);

const SoftComplete: React.FC<{
  detail: SurveyDetail;
  completedAtLabel: string | null;
  language: 'en' | 'es';
  primaryColor: string;
  onExit: () => void;
}> = ({ detail, completedAtLabel, language, primaryColor, onExit }) => (
  <div
    className="rounded-3xl overflow-hidden p-8 text-center flex flex-col items-center gap-3 dark:ring-1 dark:ring-white/10"
    style={{ backgroundColor: `${primaryColor}12` }}
  >
    <div
      className="size-12 rounded-full flex items-center justify-center"
      style={{ backgroundColor: `${primaryColor}25`, color: primaryColor }}
      aria-hidden="true"
    >
      <FilledCircleCheck className="size-7" />
    </div>
    <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-gray-900 dark:text-gray-100">
      {language === 'es' ? '¡Listo!' : 'All done'}
    </h1>
    <p className="max-w-prose text-sm text-gray-600 dark:text-gray-300">
      {detail.completedAt && completedAtLabel
        ? `${language === 'es' ? 'Completado el' : 'Completed on'} ${completedAtLabel}.`
        : detail.survey.endMessage || (language === 'es' ? 'Gracias.' : 'Thank you.')}
    </p>
    <TolerantTouchButton
      onPress={onExit}
      className="mt-2 inline-flex items-center justify-center rounded-full px-6 py-2.5 text-sm font-semibold text-white"
      style={{ backgroundColor: primaryColor }}
    >
      {language === 'es' ? 'Volver al inicio' : 'Back to home'}
    </TolerantTouchButton>
  </div>
);

export default SurveyRunner;
